import { useEffect, useState } from 'react';
import { updateUrgencySignalsPageProperties } from '@/utils/dataLayer';
import { useDisplayStatus } from '@/components/_shared/contexts/DisplayStatusContext';
import { HighLevelStatus } from '@/utils/highLevelStatus';
import type { BadgeInfo } from './badgeInfo';
import { badges } from './badges';
import type { Signal } from './signal';
import { UrgencySignalBadge } from './UrgencySignalBadge';

const MAX_VISIBLE_SIGNALS = 1;

const URGENCY_SIGNALS_SUPPRESSED_STATUSES = new Set<HighLevelStatus>([
  HighLevelStatus.SOLD_OUT,
  HighLevelStatus.SALES_ENDED,
  HighLevelStatus.CANCELLED,
  HighLevelStatus.EVENT_ENDED,
]);

interface Props {
  eventId: string;
}

export const UrgencySignals: React.FC<Props> = ({ eventId }) => {
  const { displayStatus } = useDisplayStatus();
  const suppressUrgencySignals = URGENCY_SIGNALS_SUPPRESSED_STATUSES.has(displayStatus);
  const urgencySignals = useUrgencySignals(eventId, suppressUrgencySignals);

  if (suppressUrgencySignals) {
    return null;
  }

  const visibleBadges = getVisibleBadgesFrom(urgencySignals.slice(0, MAX_VISIBLE_SIGNALS));
  return visibleBadges.map((badge) => <UrgencySignalBadge key={badge.signal} badge={badge} />);
};

function useUrgencySignals(eventId: string, suppressUrgencySignals: boolean) {
  const [urgencySignals, setUrgencySignals] = useState<Signal[]>([]);

  useEffect(() => {
    if (suppressUrgencySignals) {
      setUrgencySignals([]);
      updateUrgencySignalsPageProperties([]);
      return;
    }

    fetch(`/e/api/${eventId}/urgency-signals`)
      .then(async (res) => {
        if (!res.ok) {
          setUrgencySignals([]);
          updateUrgencySignalsPageProperties([]);
          return;
        }
        const newSignals: string[] = await res.json();
        setUrgencySignals(newSignals as Signal[]);
        updateUrgencySignalsPageProperties(newSignals);
      })
      .catch(() => {
        setUrgencySignals([]);
        updateUrgencySignalsPageProperties([]);
      });
  }, [eventId, suppressUrgencySignals]);

  return urgencySignals;
}

function getVisibleBadgesFrom(urgencySignals: Signal[]): Array<BadgeInfo> {
  const presentBadges = badges
    .sort((a, b) => a.priority - b.priority)
    .filter((badge) => urgencySignals.includes(badge.signal));

  const messageBadge = presentBadges.filter((badge) => badge.type === 'message')[0];
  const categoryBadges = presentBadges.filter((badge) => badge.type === 'category');

  if (messageBadge) {
    return [messageBadge, ...categoryBadges.slice(0, 1)];
  }

  return categoryBadges.slice(0, 2);
}
